import * as yup from "yup";
import { sourceRequiresChannel } from "../constants/leadSourceOptions";

function toPhoneDigits(s: string): string {
  return (s ?? "").replace(/\D/g, "");
}

const optionalEmail = yup
  .string()
  .trim()
  .test("email", "Enter a valid email", (value) => {
    if (!value || !value.trim()) return true;
    return yup.string().email().isValidSync(value.trim());
  });

const optionalWhatsapp = yup
  .string()
  .trim()
  .test(
    "whatsapp",
    "Enter a valid WhatsApp number (min 10 digits)",
    (value) => {
      if (!value || !value.trim()) return true;
      const digits = toPhoneDigits(value);
      return digits.length >= 10 && digits.length <= 15;
    }
  );

const optionalDisplayNumber = yup
  .string()
  .trim()
  .test(
    "displayNumber",
    "Enter a valid display number (min 10 digits)",
    (value) => {
      if (!value || !value.trim()) return true;
      const digits = toPhoneDigits(value);
      return digits.length >= 10 && digits.length <= 15;
    }
  );

/**
 * Add New Contact / Edit Contact form rules.
 * CAR-2816: Gender and Address (search, city, state, pincode) are optional.
 * Patient Registration is a different form and keeps its own required rules.
 */
export const addNewContactValidationSchema = yup.object({
  patientName: yup.string().trim().required("Patient Name is required"),
  gender: yup.string().trim().optional().nullable(),
  phoneNumber: yup
    .string()
    .trim()
    .required("Phone Number is required")
    .matches(/^\+?[\d\s-]{10,14}$/, "Enter a valid phone number"),
  email: optionalEmail,
  displayNumber: optionalDisplayNumber,
  whatsappNumber: optionalWhatsapp,
  dob: yup.string().optional().nullable(),
  age: yup.string().optional().nullable(),
  searchAddress: yup.string().trim().optional().nullable(),
  city: yup.string().trim().optional().nullable(),
  state: yup.string().trim().optional().nullable(),
  country: yup.string().optional().nullable(),
  pincode: yup
    .string()
    .trim()
    .optional()
    .nullable()
    .matches(/^\d{6}$/, {
      excludeEmptyString: true,
      message: "Pincode must be 6 digits",
    }),
  unit: yup.string().required("Unit is required"),
  source: yup.string().optional().nullable(),
  channel: yup
    .string()
    .nullable()
    .when("source", {
      is: (source: string | null | undefined) =>
        sourceRequiresChannel(source ?? ""),
      then: (schema) => schema.trim().required("Channel is required"),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  preferredUnit: yup.string().optional().nullable(),
  payerType: yup.string().optional().nullable(),
  opportunity: yup.string().optional().nullable(),
});

/**
 * Patient 360 edit (CAR-2844): only CRM fields are writable.
 * Skip required / format checks on HIS-owned locked fields (address, pincode,
 * unit, registered phone) so Save works when those values are missing or
 * non-canonical. Custom Display Number still requires 10–15 digits unless it
 * mirrors the locked registered phone (Same as phone).
 */
const patientCrmDisplayNumber = yup
  .string()
  .trim()
  .test(
    "displayNumber",
    "Enter a valid display number (min 10 digits)",
    function (value) {
      if (!value || !value.trim()) return true;
      const digits = toPhoneDigits(value);
      const phoneDigits = toPhoneDigits(
        String(
          (this.parent as { phoneNumber?: string | null }).phoneNumber ?? ""
        )
      );
      // Locked phone mirrored into display — do not block Save on short/invalid HIS phone.
      if (phoneDigits.length > 0 && digits === phoneDigits) return true;
      return digits.length >= 10 && digits.length <= 15;
    }
  );

export const editPatientCrmValidationSchema = yup.object({
  patientName: yup.string().trim().optional().nullable(),
  gender: yup.string().trim().optional().nullable(),
  phoneNumber: yup.string().trim().optional().nullable(),
  email: optionalEmail,
  displayNumber: patientCrmDisplayNumber,
  whatsappNumber: optionalWhatsapp,
  dob: yup.string().optional().nullable(),
  age: yup.string().optional().nullable(),
  searchAddress: yup.string().trim().optional().nullable(),
  city: yup.string().trim().optional().nullable(),
  state: yup.string().trim().optional().nullable(),
  country: yup.string().optional().nullable(),
  // Locked HIS field — never block Save on format (may be "NA", short, etc.).
  pincode: yup.string().trim().optional().nullable(),
  unit: yup.string().optional().nullable(),
  source: yup.string().optional().nullable(),
  channel: yup.string().optional().nullable(),
  preferredUnit: yup.string().optional().nullable(),
  payerType: yup.string().optional().nullable(),
  opportunity: yup.string().optional().nullable(),
});
